import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ProjectContext = createContext(null);
const STORAGE_KEY = "rrl.activeProjectId";

export function ProjectProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [activeProjectId, _setActiveProjectId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [loading, setLoading] = useState(true);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
      const stored = localStorage.getItem(STORAGE_KEY);
      const exists = stored && data.find((p) => p.id === stored);
      if (!exists && data.length > 0) {
        localStorage.setItem(STORAGE_KEY, data[0].id);
        _setActiveProjectId(data[0].id);
      } else if (exists) {
        _setActiveProjectId(stored);
      }
    } catch (err) {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch projects only after auth resolves AND a user is signed in.
  // Re-fetch whenever the authenticated user changes (login/logout/switch).
  useEffect(() => {
    if (authLoading) return;
    if (user && user.id) {
      loadProjects();
    } else {
      setProjects([]);
      setLoading(false);
    }
  }, [user, authLoading, loadProjects]);

  const setActiveProjectId = (id) => {
    localStorage.setItem(STORAGE_KEY, id);
    _setActiveProjectId(id);
  };

  const activeProject = projects.find((p) => p.id === activeProjectId) || null;

  return (
    <ProjectContext.Provider value={{ projects, activeProjectId, activeProject, setActiveProjectId, loading, refresh: loadProjects }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error("useProject must be used within ProjectProvider");
  return ctx;
}
